import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDateTime, formatMoney } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import { verfolgungsLink } from '../versender';

interface Auftragszeiten {
  id: string;
  number: string;
  owner_name: string;
  customer_name: string | null;
  status: string;
  angelegt: string;
  pickliste: string | null;
  erster_griff: string | null;
  letzter_griff: string | null;
  packen_begonnen: string | null;
  versendet: string | null;
  positionen: number;
  einheiten: number;
  pickbuchungen: number;
  beteiligte: number;
  packstuecke: number | null;
  carrier: string | null;
  versender: string | null;
  tracking_ref: string | null;
  verfolgung: string | null;
  vorbereitung: number | null;
  wartezeit: number | null;
  kommissionierung: number | null;
  zum_packtisch: number | null;
  packen: number | null;
  nach_kommissionierung: number | null;
  gesamt: number | null;
}

interface Kennzahl {
  bezeichnung: string;
  hinweis?: string;
  median: number | null;
  mittel: number | null;
  langsamste: number | null;
  faelle: number;
}

interface JeAuftraggeber {
  owner_id: string;
  owner_name: string;
  auftraege: number;
  positionen: number;
  einheiten: number;
  median_gesamt: number | null;
  handling_out_cents: number | null;
}

interface Auswertung {
  von: string;
  bis: string;
  auftraege: Auftragszeiten[];
  kennzahlen: Kennzahl[];
  jePosition: number | null;
  jeEinheit: number | null;
  ausgewertet: number;
  ohneVersand: number;
  jeAuftraggeber: JeAuftraggeber[];
  ab: string | null;
}

/** Sekunden als Dauer, die man vorlesen kann. */
function dauer(sekunden: number | null): string {
  if (sekunden === null || sekunden < 0) return '—';
  if (sekunden < 60) return `${sekunden} s`;
  const minuten = Math.round(sekunden / 60);
  if (minuten < 90) return `${minuten} min`;
  const stunden = sekunden / 3600;
  if (stunden < 48) return `${stunden.toFixed(1).replace('.', ',')} h`;
  return `${Math.round(stunden / 24)} Tage`;
}

const heute = () => new Date().toISOString().slice(0, 10);
const vorTagen = (n: number) =>
  new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

export function LeadtimesPage() {
  const [daten, setDaten] = useState<Auswertung | null>(null);
  const [von, setVon] = useState(vorTagen(30));
  const [bis, setBis] = useState(heute());
  const [stundensatz, setStundensatz] = useState('32,00');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      setDaten(await api<Auswertung>(`/api/outbound/durchlaufzeiten?von=${von}&bis=${bis}`));
    } catch (err) {
      setError(errorText(err));
    }
  }, [von, bis]);

  useEffect(() => {
    void load();
  }, [load]);

  const satzCents = Math.round(
    (Number(stundensatz.replace(',', '.')) || 0) * 100,
  );

  if (!daten) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Card title="Zeitraum">
        <div className="form-grid">
          <TextField label="Von" value={von} onChange={setVon} hint="JJJJ-MM-TT" />
          <TextField label="Bis" value={bis} onChange={setBis} />
          <TextField
            label="Stundensatz Lager"
            value={stundensatz}
            onChange={setStundensatz}
            hint="für die Gegenrechnung zur Handling-Pauschale"
          />
        </div>
        <div className="small muted">
          {daten.ausgewertet} Aufträge ausgewertet
          {daten.ohneVersand > 0 && `, davon ${daten.ohneVersand} noch nicht versendet`}
          {daten.ab && ` · Aufzeichnung beginnt am ${daten.ab}`}
        </div>
      </Card>

      <Card title="Wie lange dauert was">
        <div className="small muted" style={{ marginBottom: 14 }}>
          Gerechnet aus dem, was ohnehin protokolliert ist — Auftragsanlage, Pickliste, jede
          einzelne Entnahme, Packbeginn und Versand. Der <strong>Median</strong> steht vorn, weil
          ein einziger über Nacht liegengebliebener Auftrag jeden Mittelwert unbrauchbar macht.
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Abschnitt</th>
                <th className="num">Median</th>
                <th className="num">Mittel</th>
                <th className="num">Langsamster</th>
                <th className="num">Fälle</th>
              </tr>
            </thead>
            <tbody>
              {daten.kennzahlen.map((k) => (
                <tr key={k.bezeichnung}>
                  <td>
                    {k.bezeichnung}
                    {k.hinweis && <span className="small muted"> — {k.hinweis}</span>}
                  </td>
                  <td className="num">
                    <strong>{dauer(k.median)}</strong>
                  </td>
                  <td className="num muted">{dauer(k.mittel)}</td>
                  <td className="num muted">{dauer(k.langsamste)}</td>
                  <td className="num muted">{k.faelle}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="small muted" style={{ marginTop: 12 }}>
          Je Position {dauer(daten.jePosition)} · je Einheit {dauer(daten.jeEinheit)}
        </div>
      </Card>

      <Card title="Deckt die Pauschale den Aufwand?">
        <div className="small muted" style={{ marginBottom: 14 }}>
          Die Handling-Pauschale aus dem Tarif gegen die tatsächlich gemessene Zeit, gerechnet mit
          dem oben eingetragenen Stundensatz. Die Zeit umfasst den ganzen Durchlauf vom Auftrag bis
          zum Versand — wer nur die reine Packzeit gegenrechnen will, nimmt die Zeile
          <em> Packen</em> aus der Tabelle darüber.
        </div>
        {daten.jeAuftraggeber.length === 0 ? (
          <Empty>Im Zeitraum wurden keine Aufträge abgewickelt.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Auftraggeber</th>
                  <th className="num">Aufträge</th>
                  <th className="num">Median</th>
                  <th className="num">Kosten je Auftrag</th>
                  <th className="num">Pauschale</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {daten.jeAuftraggeber.map((zeile) => {
                  const kosten =
                    zeile.median_gesamt === null || zeile.median_gesamt < 0
                      ? null
                      : Math.round((zeile.median_gesamt / 3600) * satzCents);
                  const pauschale = zeile.handling_out_cents;
                  const differenz =
                    kosten === null || pauschale === null ? null : pauschale - kosten;
                  return (
                    <tr key={zeile.owner_id}>
                      <td>{zeile.owner_name}</td>
                      <td className="num">{zeile.auftraege}</td>
                      <td className="num">{dauer(zeile.median_gesamt)}</td>
                      <td className="num">{kosten === null ? '—' : formatMoney(kosten)}</td>
                      <td className="num">
                        {pauschale === null ? (
                          <span className="muted">kein Tarif</span>
                        ) : (
                          formatMoney(pauschale)
                        )}
                      </td>
                      <td>
                        {differenz === null ? (
                          <span className="muted">—</span>
                        ) : differenz >= 0 ? (
                          <Badge kind="ok">deckt {formatMoney(differenz)}</Badge>
                        ) : (
                          <Badge kind="bad">fehlen {formatMoney(-differenz)}</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="small muted" style={{ marginTop: 12 }}>
          Der Durchlauf enthält Liege- und Wartezeit. Als Kostengrundlage taugt er erst, wenn am
          Packtisch die Zeit genommen wird — bis dahin ist die Zahl eine Obergrenze, kein Preis.
        </div>
      </Card>

      <Card title="Einzelne Aufträge" padded={false}>
        {daten.auftraege.length === 0 ? (
          <Empty>Im Zeitraum wurde nichts abgewickelt.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Auftrag</th>
                  <th>Auftraggeber</th>
                  <th className="num">Pos.</th>
                  <th className="num">Komm.</th>
                  <th className="num">Packen</th>
                  <th className="num">Gesamt</th>
                  <th>Sendung</th>
                </tr>
              </thead>
              <tbody>
                {daten.auftraege.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className="mono">{a.number}</span>
                      <div className="small muted">{formatDateTime(a.angelegt)}</div>
                    </td>
                    <td className="small">
                      {a.owner_name}
                      {a.customer_name && <div className="muted">an {a.customer_name}</div>}
                    </td>
                    <td className="num">{a.positionen}</td>
                    <td className="num">{dauer(a.kommissionierung)}</td>
                    <td className="num">
                      {a.packen !== null ? (
                        dauer(a.packen)
                      ) : (
                        <span className="muted" title="ohne erfassten Packbeginn">
                          ({dauer(a.nach_kommissionierung)})
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <strong>{dauer(a.gesamt)}</strong>
                    </td>
                    <td className="small">
                      {a.tracking_ref ? (
                        a.verfolgung ? (
                          <a href={a.verfolgung} target="_blank" rel="noreferrer noopener">
                            {a.tracking_ref}
                          </a>
                        ) : (
                          <span className="mono">{a.tracking_ref}</span>
                        )
                      ) : (
                        <span className="muted">—</span>
                      )}
                      {a.versender && <div className="muted">{a.versender}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
